import type { AvatarData } from './generated/avatar-runtime';
import type { AnimationName as ExportedAnimationName } from './generated/strobi.avatar';

/**
 * Expressions 25 and 26 from the Avatar Lab document. They were added after
 * the standalone mascot exports, so the application enriches each copied
 * export without mutating the generated source files.
 */
const losingAngryExpression = {
  id: 'expression-3d2bed26-f97c-477d-922f-77600cb10e92',
  headX: 10.473974503042374,
  headY: 5.087293619785961,
  headZ: 4.698252132317348,
  widthLeft: 27.126562499999995,
  widthRight: 27.126562499999995,
  heightLeft: 63.028125,
  heightRight: 63.028125,
  spacing: 68.7,
  positionXLeft: 0,
  positionXRight: 0,
  positionYLeft: 0,
  positionYRight: 0,
  leftAngle: -36.244531249999994,
  rightAngle: 27.730078125000002,
  perspective: 1,
  eyeMotion: 'none',
  bodyMotion: 'shake',
  bodyColor: '#ba3636',
  eyeColor: '#610000',
} as const;

const lastPlaceExpression = {
  id: 'expression-5220eaee-32fe-4bd8-ad31-432189534cc8',
  headX: -12.303515625,
  headY: -17.601171875,
  headZ: 5.9109375,
  widthLeft: 20.605859374999994,
  widthRight: 20.605859374999994,
  heightLeft: 47.769921874999994,
  heightRight: 47.769921874999994,
  spacing: 54.9,
  positionXLeft: 0,
  positionXRight: 0,
  positionYLeft: 0,
  positionYRight: 0,
  leftAngle: 23.523046875000002,
  rightAngle: -24.042578125000002,
  perspective: 1,
  eyeMotion: 'shake',
  bodyMotion: 'slowDrift',
  bodyColor: '#adc3ff',
} as const;

const contextualAnimations = {
  'losing-angry': {
    name: 'losing-angry',
    description: 'Expression 25 : colère après une série de manches à zéro.',
    playbackMode: 'loop',
    blink: {
      enabled: true,
      initialDelayMs: 2100,
      minIntervalMs: 2800,
      maxIntervalMs: 5000,
      durationMs: 260,
    },
    steps: [{
      expressionId: losingAngryExpression.id,
      holdMs: 3000,
      transitionMs: 360,
      transition: 'smooth',
    }],
  },
  'last-place': {
    name: 'last-place',
    description: 'Expression 26 : déception du dernier au classement final.',
    playbackMode: 'loop',
    blink: {
      enabled: true,
      initialDelayMs: 4800,
      minIntervalMs: 6500,
      maxIntervalMs: 9500,
      durationMs: 420,
    },
    steps: [{
      expressionId: lastPlaceExpression.id,
      holdMs: 3600,
      transitionMs: 500,
      transition: 'smooth',
    }],
  },
} as const;

export type MascotAnimationName = ExportedAnimationName | keyof typeof contextualAnimations;

export function usesContextAnimationPalette(animation: string): boolean {
  return animation === 'losing-angry' || animation === 'last-place';
}

export function withContextAnimations(
  data: AvatarData<ExportedAnimationName>,
): AvatarData<MascotAnimationName> {
  return {
    ...data,
    expressions: {
      ...data.expressions,
      [losingAngryExpression.id]: losingAngryExpression,
      [lastPlaceExpression.id]: lastPlaceExpression,
    },
    animations: {
      ...data.animations,
      ...contextualAnimations,
    },
  };
}
